/* Copyright (c) 2020-present CloudIO Inc */

import { SubscriptionEvent } from '@cloudio-saas/datasource-types';
import { useStateXGetter, useStateXSetter } from '@cloudio-saas/statex';
import { Box, IconButton, Typography } from '@mui/material';
import { getManager, Item, Page } from 'componentregistry';
import zIndex from 'constants/ZIndex';
import { designPath } from 'designer/atoms';
import { copyItemBetweenPage } from 'designer/state';
import useDnD from 'designer/useDnD';
import { useIORef } from 'designer/useIORef';
import Icon from 'icons/Icon';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';

export default function EventClipboard({
  appUid,
  pageId,
  event,
  clearClipboard,
}: {
  appUid: string;
  pageId: string;
  event: SubscriptionEvent;
  clearClipboard: () => void;
}): React.ReactElement | null {
  const eventRef = useIORef<HTMLElement>();
  const get = useStateXGetter();
  const set = useStateXSetter();
  const platform = usePlatform();
  const [newItemId, setNewItemId] = useState('');
  const epage = event.data as Page;
  const clipboard = epage.clipboard as Item<'Clipboard'>;
  const itemId = clipboard.children[0];
  const item = epage[itemId];
  const mgr = getManager(item.type);
  let icon = mgr.icon;
  if (typeof icon === 'function') {
    icon = icon(itemWithDefaultValues(item));
  }

  useDnD({
    appUid,
    pageId,
    item,
    ref: eventRef,
    callback: () => {
      let page = get(designPath({ appUid, pageId })) as Page;
      page = { ...page };
      const newId = copyItemBetweenPage({
        fromPage: epage,
        itemId,
        page,
        newParentId: '',
        retainRootItemId: true,
        retainAllItemIds: true,
        platform,
      });
      page[newId].parent = '';
      setNewItemId(newId);
      set(designPath({ appUid, pageId }), page);
    },
    endCallback: () => {
      const page = get(designPath({ appUid, pageId })) as Page;
      if (page[newItemId]) {
        clearClipboard();
      }
    },
    disabled: !event,
  });

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        boxShadow: 4,
        color: 'text.secondary',
        right: 8,
        p: 0,
        position: 'absolute',
        top: 8,
        zIndex: zIndex.DeveloperToolbar + 1,
        display: 'flex',
        width: 240,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '0 8px',
      }}
    >
      <Box
        ref={eventRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '8px',
          cursor: 'grab',
          flex: 1,
        }}
      >
        <Icon {...icon} />
        <Typography>{mgr.name}</Typography>
      </Box>
      <IconButton data-tip="Clear Clipboard" onClick={clearClipboard}>
        <Icon iconName="trash" />
      </IconButton>
    </Box>
  );
}
